import styled from "styled-components";
import { useBudgetState } from "hooks/useBudgetState";
import { useCalendarState } from "hooks/useCalendarState";
import React from "react";
import { setEventsExpanded } from '../events/eventsState';

const prioritiesData = [
  {
    id: 1,
    title: "Revenue Goals",
    status: "Available",
    progress: 0,
    active: true,
    color: "#1CCCA4",
    description: "Set your revenue goals and track your Revenue progress."
  },

  {
    id: 2,
    title: "RSVP to 3 Events",
    status: "Available",
    progress: 0,
    active: true,
    color: "#21fffc", // Royal Blue
    description: "RSVP to at least 3 events, webinar or otherwise."
  },

  {
    id: 3,
    title: "Setup Challenges",
    status: "Coming Soon",
    progress: 0,
    active: false,
    color: "#1CCCA4",
    description: "Use the Challenges Section to set your revenue goals and track your progress."
  },
  {
    id: 4,
    title: "Pitch Formula",
    status: "Coming Soon",
    progress: 0,
    active: false,
    color: "#1CCCA4",
    description: "Use the Pitch Formula Section to set your revenue goals and track your progress."
  },
];

const isProductComplete = (product) => {
  return product.cost > 0 &&
    product.unitsYear1 > 0 &&
    product.unitsYear2 > 0 &&
    product.unitsYear3 > 0;
};

const getProductProgress = (product) => {
  const fields = [
    product.cost > 0,
    product.unitsYear1 > 0,
    product.unitsYear2 > 0,
    product.unitsYear3 > 0
  ];
  const filledFields = fields.filter(Boolean).length;
  return (filledFields / fields.length) * 100;
};

const getCategoryProgress = (category) => {
  // Base progress just for having a category
  const baseProgress = 10;

  if (!category.products.length) return baseProgress;

  // Calculate progress for each product
  const productProgresses = category.products.map(getProductProgress);

  // Sort by highest progress to focus on most complete products
  const sortedProgresses = [...productProgresses].sort((a, b) => b - a);

  // We need at least 2 products for full completion
  const targetProductCount = 2;

  // Calculate category completion percentage
  const productsWithProgress = productProgresses.filter(progress => progress > 0).length;
  const productCountProgress = Math.min(productsWithProgress / targetProductCount, 1) * 100;

  // Calculate average progress of top 2 products (or fewer if not enough products)
  const topProductsProgress = sortedProgresses
    .slice(0, targetProductCount)
    .reduce((acc, progress) => acc + progress, 0) / targetProductCount;

  // Combine all factors:
  // - Base progress for having a category - 10%
  // - How many products we have (up to 2) - 35% weight
  // - Average completion of top products - 55% weight
  return baseProgress + (productCountProgress * 0.35) + (topProductsProgress * 0.55);
};

const getOverallProgress = (categories) => {
  if (!categories?.length) return 0;

  // Calculate progress for each category
  const categoryProgresses = categories.map(getCategoryProgress);

  // Sort categories by progress to focus on most complete ones
  const sortedProgresses = [...categoryProgresses].sort((a, b) => b - a);

  // Take the top two categories' progress
  const topTwoProgress = sortedProgresses.slice(0, 2);

  if (topTwoProgress.length === 1) {
    // If we only have one category with progress, cap at 50%
    return Math.min(Math.round(topTwoProgress[0]), 50);
  }

  // With two or more categories, average their progress
  const averageTopProgress = topTwoProgress.reduce(
    (acc, progress) => acc + progress,
    0
  ) / 2;

  // Round to nearest whole number
  return Math.round(averageTopProgress);
};

export default function Priorities() {
  const { budgetState } = useBudgetState();
  const { rsvpState, isLoading } = useCalendarState();
  const [expandedIds, setExpandedIds] = React.useState(new Set());

  // Calculate progress for Revenue Goals based on budget state
  const revenueProgress = React.useMemo(() => {
    if (!budgetState?.categories) return 0;
    return getOverallProgress(budgetState.categories);
  }, [budgetState]);

  // Calculate progress for Events RSVPs
  const eventProgress = React.useMemo(() => {
    if (isLoading) return 0;
    const rsvpCount = Object.values(rsvpState || {}).filter(status => status === true).length;
    const targetRsvps = 3;
    return Math.min(Math.round((rsvpCount / targetRsvps) * 100), 100);
  }, [rsvpState, isLoading]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        // If we're collapsing the Events priority, collapse the events section too
        if (id === 2) {
          setEventsExpanded(false);
        }
      } else {
        newSet.add(id);
        // If we're expanding the Events priority, expand the events section too
        if (id === 2) {
          setEventsExpanded(true);
        }
      }
      return newSet;
    });
  };

  // Update prioritiesData with the calculated progress
  const updatedPriorities = React.useMemo(() => {
    return prioritiesData.map(priority => {
      if (priority.id === 1) { // Revenue Goals
        const hasCategories = budgetState?.categories?.length > 0;
        const status = !hasCategories ? "Available" :
          revenueProgress === 100 ? "Done!" :
            "In Progress";

        return {
          ...priority,
          progress: revenueProgress,
          status
        };
      }
      
      if (priority.id === 2) { // Events RSVPs
        const rsvpCount = Object.values(rsvpState).filter(status => status === true).length;
        const status = rsvpCount === 0 ? "Available" :
          eventProgress === 100 ? "Complete" :
            "In Progress";

        return {
          ...priority,
          progress: eventProgress,
          status
        };
      }

      return priority;
    });
  }, [revenueProgress, eventProgress, budgetState, rsvpState]);

  const inProgressCount = updatedPriorities.filter(item =>
    item.active && (item.status === "In Progress" || item.status === "Complete" || item.status === "Done!")
  ).length;

  const totalActivePriorities = updatedPriorities.filter(item =>
    item.active
  ).length;

  return (
    <PrioritiesContainer className="Priorities">
      <Header>
        <Title>TODOs</Title>
        <Count>{inProgressCount} / {totalActivePriorities} Active</Count>
      </Header>
      <Content>
        {updatedPriorities.map((item) => (
          <PriorityItem 
            key={item.id} 
            $active={item.active}
            onClick={() => item.active && toggleExpand(item.id)}
            $isExpanded={expandedIds.has(item.id)}
          >
            <ItemHeader>
              <ItemTitle>{item.title}</ItemTitle>
              <Status color={item.color}>{item.status}</Status>
            </ItemHeader>
            <Progress>
              <ProgressBar width={item.progress} color={item.color} />
            </Progress>
            {expandedIds.has(item.id) && (
              <Description>{item.description}</Description>
            )}
          </PriorityItem>
        ))}
      </Content>
    </PrioritiesContainer>
  );
}

const PrioritiesContainer = styled.div`
  background: #111111;
  border-radius: 5px;
  padding: 15px;
  color: white;
  height: fit-content;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 0px 9px 0px 10px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const Description = styled.p`
  font-size: 12px;
  font-weight: 400;
  margin-top: 12px;
  text-align: left;
  font-family: "PT Sans";
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Count = styled.span`
  background: #222;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const PriorityItem = styled.div`
  background: #222;
  border-radius: 0px;
  padding: 12px;
  transition: all 0.3s ease;
  opacity: ${props => props.$active ? 1 : 0.3};
  cursor: ${props => props.$active ? 'pointer' : 'not-allowed'};
  
  &:first-of-type {
    border-radius: 4px 4px 0 0;
  }
  &:last-of-type {
    border-radius: 0 0 4px 4px;
  }
  &:hover {
    background: ${props => props.$active ? '#2a2a2a' : '#222'};
    transform: ${props => props.$active ? 'translateY(-1px)' : 'none'};
  }
  
  ${props => props.$isExpanded && `
    background: #2a2a2a;
  `}
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ItemTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
`;

const Status = styled.span`
  font-size: 11px;
  background: #333;
  padding: 2px 8px;
  border-radius: 12px;
  color: ${props => props.color || '#1CCCA4'};
  font-weight: ${props => props.$active ? 300 : 600};
  animation: ${props => props.children === "Complete" ? 'celebratePulse 1.5s ease-out' : 'none'};
  animation-iteration-count: ${props => props.children === "Complete" ? '2' : '1'};

  @keyframes celebratePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(28, 204, 164, 0.2);
      font-weight: 600;
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(28, 204, 164, 0);
      font-weight: 800;
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(28, 204, 164, 0);
      font-weight: 600;
    }
  }
`;

const Progress = styled.div`
  background: #333;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  width: ${props => props.width}%;
  height: 100%;
  background-color: ${props => props.color || '#1CCCA4'};
  transition: width 0.3s ease;
`;